import { number_from_query, str_from_query } from "@/api";
import { axios_instance } from "@/api/api";
import { router } from "@/router/router";
import { useQueryClient } from "@tanstack/vue-query";
import { defineStore } from "pinia";
import { computed, ref, watch } from "vue";
import { toast } from "vue3-toastify";
import { parse_windows_path, type action, type client_file, type extension_bucket } from ".";
import { use_action } from "./useAction";

export const use_explorer = defineStore("explorer", () => {
  const size_from_query = (key: string) => {
    const value = str_from_query(key);
    if (value === undefined || value === "") return undefined;
    const size = Number(value);
    return Number.isSafeInteger(size) && size >= 0 ? size : undefined;
  };
  // this is used to determine if we are in correct route for explorer logic
  const route_name = "ClientExplorer";
  const upload_file = ref<File>();
  const queryClient = useQueryClient();
  const is_in_explore_screen = (): boolean => {
    return router.currentRoute.value.name === route_name;
  };

  const init_keyword = computed(() => str_from_query("keyword"));
  const keyword = ref<string | undefined>(str_from_query("keyword"));
  const init_path = computed(() => str_from_query("path"));
  const path = ref<string | undefined>(str_from_query("path"));
  const init_extension = computed(() => str_from_query("extension"));
  const extension = ref<string | undefined>(str_from_query("extension"));
  const extensions = ref<extension_bucket[]>([]);

  const files = ref<client_file[]>([]);
  const total_filter = ref<number>(0);
  const total_pages = computed(() => {
    const size = number_from_query("size", 24);
    return Math.ceil(total_filter.value / size);
  });
  const action = ref<action>();
  const dirs = computed(() => {
    return parse_windows_path(str_from_query("path"));
  });

  const get_extensions = () => {
    const path = str_from_query("path");
    if (!path) {
      extensions.value = [];
      return;
    }

    const data = JSON.stringify({
      path: path,
      agent_id: router.currentRoute.value.params.client_id,
    });

    axios_instance
      .request<{
        data: extension_bucket[];
        message: string;
      }>({
        method: "post",
        url: "/api/v1/view/explore/scan/extensions",
        headers: {
          "Content-Type": "application/json",
        },
        data: data,
      })
      .then((response) => {
        extensions.value = response.data.data ?? [];
      })
      .catch(() => {
        extensions.value = [];
      });
  };

  const loading = ref<{
    [key: string]: boolean;
  }>({
    get_files: true,
  });

  watch(
    () => [router.currentRoute.value.query],
    () => {
      if (!is_in_explore_screen()) return;
      get_extensions();
      get_files();
    },
  );

  const get_files = () => {
    const path = str_from_query("path");
    const keyword = str_from_query("keyword");

    if (!path) {
      action.value = undefined;
      files.value = [];
      loading.value.get_files = false;
      return;
    }

    const data = JSON.stringify({
      path: path,
      agent_id: router.currentRoute.value.params.client_id,
      keyword: keyword,
      extension: str_from_query("extension"),
      min_size: size_from_query("min_size"),
      max_size: size_from_query("max_size"),
      page: number_from_query("page", 1),
      size: number_from_query("size", 24),
    });

    const config = {
      method: "post",
      maxBodyLength: Infinity,
      url: "/api/v1/view/explore/scan/latest",
      headers: {
        "Content-Type": "application/json",
      },
      data: data,
    };

    axios_instance
      .request<{
        data: {
          action: action | null | undefined;
          explores: client_file[];
        } | null;
        total_filter: number;
        message: string;
      }>(config)
      .then((response) => {
        if (
          !response.data.data ||
          response.data.message.toLowerCase() === "not found"
        ) {
          files.value = [];
          action.value = undefined;
          create_scan_action(path);
          return;
        }
        total_filter.value = response.data.total_filter;
        files.value = response.data.data.explores;
        if (!response.data.data.action) {
          create_scan_action(path);
          return;
        }
        action.value = response.data.data.action;

        if (action.value.status === "error") {
          toast.error("Tệp tin không tồn tại");
          files.value = [];
          return;
        }
        if (action.value.status === "complete") {
          // refresh the history section on left side bar
          queryClient.invalidateQueries({
            queryKey: ["action_history"],
          });
        }
      })
      .catch((error) => {
        toast.error(error);
      });
  };

  const create_scan_action = async (path: string, recursive = false) => {
    const agent_id = router.currentRoute.value.params.client_id;
    if (!agent_id) {
      toast.warn("agent_id missing");
      throw new Error("agent_id missing");
    }
    const data = JSON.stringify({
      agent_id: agent_id,
      action: "scan",
      type: "explore",
      path: path,
      args: "",
      recursive: recursive,
      old_file: "",
      new_file: "",
    });

    const config = {
      method: "post",
      maxBodyLength: Infinity,
      url: "/api/v1/view/action",
      headers: {
        "Content-Type": "application/json",
      },
      data: data,
    };

    const response = await axios_instance.request<{
      action_id: string;
      message: string;
      status: string;
    }>(config);
    if (response.status !== 200 || response.data.status === "error") {
      toast.error("Tạo yêu cầu quét thất bại");
      return;
    }

    action.value = {
      id: response.data.action_id,
      action: "scan",
      type: "explore",
      status: response.data.status,
    };
    loading.value.get_files = true;
    toast.success("Tạo yêu cầu quét thành công");
  };

  const action_store = use_action();
  const handle_delete = async (path: string) => {
    if (!confirm(`Bạn có chắc chắn muốn xóa ${path} không?`)) return;
    const action_id = await action_store.create_action({
      action: "delete",
      type: "explore",
      path: path,
    });
    toast.success("Tạo yêu cầu xóa thành công");
    if (action_id)
      action_store.watch_for_action(action_id, {
        on_complete: () => {
          toast.success("Xóa tệp tin/thư mục trên máy khách thành công");
          get_files();
        },
      });
  };
  const handle_rename = async (old_path: string, new_path: string) => {
    const action_id = await action_store.create_action({
      action: "rename",
      type: "explore",
      old_file: old_path,
      new_file: new_path,
    });
    toast.success("Tạo yêu cầu đổi tên thành công");
    if (action_id)
      action_store.watch_for_action(action_id, {
        on_complete: () => {
          toast.success("Đổi tên tệp tin/thư mục trên máy khách thành công");
          get_files();
        },
      });
  };
  const handle_execute = async (path: string, args?: string) => {
    const action_id = await action_store.create_action({
      action: "execute",
      type: "explore",
      path: path,
      args: args,
    });
    toast.success("Tạo yêu cầu thực thi thành công");
    if (action_id)
      action_store.watch_for_action(action_id, {
        on_complete: () => {
          toast.success("Thực thi tệp tin/thư mục trên máy khách thành công");
          get_files();
        },
      });
  };

  watch(
    () => [action.value],
    () => {
      if (!action.value) return;

      if (
        action.value.status === "complete" ||
        action.value.status === "error"
      ) {
        loading.value.get_files = false;
        return;
      }
      loading.value.get_files = true;
      setTimeout(() => {
        if (!is_in_explore_screen()) return;
        get_files();
      }, 1000);
    },
  );

  return {
    upload_file,
    files,
    total_filter,
    total_pages,
    action,
    dirs,
    loading,
    init_keyword,
    keyword,
    init_path,
    path,
    init_extension,
    extension,
    extensions,
    get_files,
    get_extensions,
    create_scan_action,
    handle_delete,
    handle_rename,
    handle_execute,
  };
});
